import React from 'react'

import BaseConversation from '../BaseConversation'
import Content from './Content'
import CloseConversationsModal from '../ChatModals/CloseConversationsModal'

import { findFilterID, getLatestEvent, uniqueArray } from '../../../utilities'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useTyping } from '../../../providers/TypingProvider'
import { useEnvironment } from '../../../providers/EnvironmentProvider'
import { usePresence } from '../../../hooks/usePresence'
import { useAreYouSure } from '../../../providers/AreYouSureProvider'
import { useSocket } from '../../../providers/SocketProvider'
import { useQueueState } from '../../../providers/QueueStateProvider'

const ClientConversation = ({
  ownerID,
  related,
  selected,
  onClick,
  sorting,
  latestActivity = []
}) => {
  // Gets props from Conversations

  const { allUsers, loadUser } = useUserAPI()
  const { getTyping } = useTyping()
  const { user } = useEnvironment()
  const { queryPresence } = usePresence()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const {
    api: { endSessions, rejectConversation }
  } = useSocket()
  const { handleClose } = useQueueState()

  const [owner, setOwner] = React.useState(null)
  const [hasOwner, setHasOwner] = React.useState(false)
  const [showCloseModal, setShowCloseModal] = React.useState(false)

  const { users, isNew } = React.useMemo(() => {
    const users = []

    related.forEach(convo => {
      users.push(...convo.users)
    })

    const isNew = related.some(obj => obj.isNew)

    return {
      users: uniqueArray(users),
      isNew
    }
  }, [related])

  // Gets list of typing people
  const typingNames = React.useMemo(() => {
    const typing = []
    related.forEach(obj => typing.push(...getTyping(obj.session)))
    return typing
  }, [related, getTyping])

  // Comma separated list of user names
  const allNames = React.useMemo(() => {
    const names = []
    users.forEach(id => {
      const user = allUsers[id]
      if (user) {
        names.push(user.name)
      }
    })
    return names.join(', ')
  }, [allUsers, users])

  // Finds name for the session
  const sessionName = React.useMemo(() => {
    // No owner, show everyone's name
    if (!hasOwner) {
      return allNames
    }

    // Owner is being loaded
    if (!owner?.name) {
      return 'Loading...'
    }

    // Owner name has loaded now
    return owner.name
  }, [hasOwner, allNames, owner])

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const latests = []

    related.forEach(obj => {
      const filter = findFilterID(ownerID, false, obj.session)
      const latest = getLatestEvent(
        {
          ...obj,
          latestActivity
        },
        filter
      )
      latests.push(latest)
    })

    if (!latests.length) {
      return null
    }

    return latests.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
  }, [related, ownerID, latestActivity])

  // Load all users of this session if needed
  React.useEffect(() => {
    users.forEach(id => loadUser(id))
  }, [users, loadUser])

  // Load user if needed
  React.useEffect(() => {
    if (ownerID) {
      loadUser(ownerID)
    }
    setHasOwner(Boolean(ownerID))
  }, [loadUser, ownerID])

  // Set owner when loaded
  React.useEffect(() => {
    const user = allUsers[ownerID]
    if (user) {
      setOwner(user)
    }
  }, [allUsers, ownerID])

  // Updates presence
  React.useEffect(() => {
    if (selected && user) {
      const others = users.filter(id => id !== user.id)
      queryPresence(others)
    }
  }, [selected, user, users, queryPresence])

  const handleCloseConversation = React.useCallback(() => {
    // Open modal to close more than 1 conversation
    if (related.length > 1) {
      setShowCloseModal(true)
      return
    }

    // Single session closes normally
    const { session, users, ended_at } = related[0]
    const isYours = users.includes(user?.id)

    const action = () => {
      handleClose(session, () => {
        if (isYours) {
          endSessions([session], true)
        } else {
          rejectConversation(session)
        }
      })
    }

    if (isYours && !ended_at) {
      setTitle('ARE YOU SURE?')
      setReason('Closing this conversation will also end it.')
      setSubtext('')
      setAction(action)
      setV2(true)
    } else {
      action()
    }
  }, [
    related,
    user,
    setTitle,
    setReason,
    setSubtext,
    setAction,
    setV2,
    handleClose,
    endSessions,
    rejectConversation
  ])

  const avatarProps = React.useMemo(
    () => ({ userIDs: [ownerID].filter(Boolean) }),
    [ownerID]
  )

  const contentProps = React.useMemo(
    () => ({
      name: sessionName,
      selected,
      lastMessage,
      isNew,
      typingNames,
      numUsers: users.length,
      handleCloseConversation,
      users,
      isYours: users.includes(user?.id),
      related,
      ownerID
    }),
    [
      handleCloseConversation,
      isNew,
      lastMessage,
      ownerID,
      related,
      selected,
      sessionName,
      typingNames,
      user,
      users
    ]
  )

  return (
    <>
      <BaseConversation
        avatarProps={avatarProps}
        contentProps={contentProps}
        onClick={onClick}
        sorting={sorting}
        scrollID={related[0]?.session ?? ownerID}
        ContentComponent={Content}
      />

      {showCloseModal && (
        <CloseConversationsModal
          related={related}
          onClose={() => setShowCloseModal(false)}
        />
      )}
    </>
  )
}

export default ClientConversation
